import styled from "styled-components";

export const Container = styled.div`
  padding: 1rem 0;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const ComponentContainer = styled.div`
  gap: 1rem;
  display: flex;
  flex-direction: column;
`;

export const ComponentVariantContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

export const ComponentTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Section = styled.section`
  ${({ theme }) => `
    margin-bottom: 2rem;
    padding: 1.5rem;
    border: 1px solid ${theme.tokens.color.border.primary};
    border-radius: 0.5rem;
    background: ${theme.tokens.color.background.primary};
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `}
`;

export const ComponentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const ComponentCard = styled.div`
  ${({ theme }) => `
    padding: 1rem;
    border: 1px solid ${theme.tokens.color.border.primary};
    border-radius: 0.5rem;
    background: ${theme.tokens.color.background.primary};
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `}
`;

export const ComponentTitle = styled.h3`
  ${({ theme }) => `
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: ${theme.tokens.color.text.primary};
    font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `}
`;

export const ComponentDescription = styled.p`
  ${({ theme }) => `
    margin: 0;
    font-size: 0.875rem;
    color: ${theme.tokens.color.text.secondary};
    font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `}
`;

export const ComponentDemo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
`;

export const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  
  > *:nth-child(1) {
    height: 2rem;
  }
  
  > *:nth-child(2) {
    height: 1.5rem;
  }
  
  > *:nth-child(3) {
    height: 1.25rem;
  }
  
  > *:nth-child(4) {
    height: 1rem;
  }
`;

export const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

export const ColorCategory = styled.div`
  ${({ theme }) => `
    padding: 1rem;
    border: 1px solid ${theme.tokens.color.border.primary};
    border-radius: 0.5rem;
    background: ${theme.tokens.color.background.primary};
  `}
`;

export const ColorCategoryTitle = styled.h4`
  ${({ theme }) => `
    margin: 0 0 0.75rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: ${theme.tokens.color.text.primary};
    font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `}
`;

export const ColorSwatch = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const ColorBox = styled.div<{ color: string }>`
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

export const ColorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const ColorName = styled.span`
  ${({ theme }) => `
    font-size: 0.875rem;
    font-weight: 500;
    color: ${theme.tokens.color.text.primary};
    font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `}
`;

export const ColorValue = styled.span`
  ${({ theme }) => `
    font-size: 0.75rem;
    color: ${theme.tokens.color.text.secondary};
    font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-family: monospace;
  `}
`;

export const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

/** Styled link that uses theme tokens. Replaces Hangar Link to avoid theme structure mismatch (components.link.variant.base.color.surface.standard). */
export const StyledLink = styled.a<{ $size?: 'sm' | 'md' | 'lg'; $inverse?: boolean }>`
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: ${({ $size }) => ({ sm: '0.875rem', md: '1rem', lg: '1.125rem' }[$size ?? 'md'] ?? '1rem')};
  line-height: 1.5;
  color: ${({ theme, $inverse }) =>
    $inverse
      ? (theme.tokens?.color?.text?.inverse ?? '#ffffff')
      : (theme.tokens?.color?.text?.primary ?? '#0066cc')};
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  &:hover {
    opacity: 0.85;
  }
  &:focus {
    outline: 2px solid ${({ theme }) => theme.tokens?.color?.border?.focusStandard ?? '#0066cc'};
    outline-offset: 2px;
  }
`;
